import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Lightbulb, Quote, Send, RotateCcw, X } from "lucide-react";
import { fadeUp } from "@/lib/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODES = {
    ideas: {
        label: "Video Ideas",
        sub: "Fresh ideas for your next clip",
        placeholder: "What is your video about?",
        intro: "Tell me what your video is about — I'll send back simple ideas you can film today.",
    },
    captions: {
        label: "Captions",
        sub: "Words that make people watch",
        placeholder: "Describe your clip — what's happening in it?",
        intro: "Describe your clip and I'll write ready-to-post captions for it.",
    },
    hashtags: {
        label: "Hashtags",
        sub: "Tags that push you to the FYP",
        placeholder: "What is your video about?",
        intro: "Tell me what your video shows and I'll send hashtags that actually fit it.",
    },
    voiceover: {
        label: "Voice-over",
        sub: "Scripts you can read on camera",
        placeholder: "What is your video about?",
        intro: "Tell me the topic and length — I'll write a short voice-over script you can read straight away.",
    },
    tips: {
        label: "Editing Tips",
        sub: "Quick wins for better cuts",
        placeholder: "What are you editing today?",
        intro: "Tell me what you're editing and I'll send simple tips to make it feel more professional.",
    },
    title: {
        label: "Video Title",
        sub: "Names that get the click",
        placeholder: "Describe your video",
        intro: "Describe your video and I'll send punchy title options.",
    },
    description: {
        label: "Description",
        sub: "Ready-to-post write-ups",
        placeholder: "Describe your video",
        intro: "Describe your video and I'll write a short, clean description you can paste.",
    },
};

export default function AiStudio() {
    const [mode, setMode] = useState(null);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);
    const sessionRef = useRef(Date.now().toString(36) + Math.random().toString(36).slice(2));
    const scrollRef = useRef(null);

    useEffect(() => {
        const el = scrollRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [messages, busy, mode]);

    const pickMode = (m) => {
        setMode((cur) => (cur === m ? cur : m));
        setMessages([]);
        setInput("");
    };

    const resetChat = () => {
        setMessages([]);
        setInput("");
    };

    const send = async () => {
        const text = input.trim();
        if (!text || busy || !mode) return;
        setInput("");
        setBusy(true);
        setMessages((m) => [...m, { role: "user", text }, { role: "ai", text: "" }]);

        const patchAi = (patch) =>
            setMessages((m) => {
                const c = [...m];
                const last = c[c.length - 1];
                if (last && last.role === "ai") c[c.length - 1] = { ...last, ...patch };
                return c;
            });

        try {
            const res = await fetch(`${API}/ai/stream`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ mode, message: text, session_id: sessionRef.current }),
            });
            if (!res.ok || !res.body) throw new Error("bad response");
            const reader = res.body.getReader();
            const dec = new TextDecoder();
            let buf = "";
            let acc = "";
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buf += dec.decode(value, { stream: true });
                let i;
                while ((i = buf.indexOf("\n\n")) >= 0) {
                    const line = buf.slice(0, i).trim();
                    buf = buf.slice(i + 2);
                    if (!line.startsWith("data:")) continue;
                    const evt = JSON.parse(line.slice(5));
                    if (evt.type === "delta") {
                        acc += evt.text;
                        patchAi({ text: acc });
                    } else if (evt.type === "error") {
                        patchAi({ text: evt.message, failed: true });
                    }
                }
            }
        } catch (e) {
            patchAi({ text: "Something went wrong on our side. Please try again in a moment.", failed: true });
        }
        setBusy(false);
    };

    return (
        <section id="ai-studio" className="border-t border-white/10 bg-surface/40 py-20 sm:py-28 lg:py-32">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <motion.div {...fadeUp} className="mx-auto max-w-2xl text-center">
                    <p className="mb-4 font-mono text-[11px] font-bold uppercase tracking-[0.3em] text-amberx">
                        AI Studio
                    </p>
                    <h2 className="font-display text-4xl font-black tracking-tight sm:text-5xl lg:text-6xl">
                        Never wonder <span className="text-amberx">what to post.</span>
                    </h2>
                    <p className="mt-4 text-base leading-relaxed text-slate-400 sm:text-lg">
                        Tap a button, say what your video is about, and get short, ready-to-use
                        answers. Free with Deoeditz.
                    </p>
                </motion.div>

                <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }} className="mx-auto mt-10 max-w-2xl">
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        {Object.entries(MODES).map(([id, m]) => {
                            const active = mode === id;
                            return (
                                <button
                                    key={id}
                                    data-testid={`ai-${id}-button`}
                                    onClick={() => pickMode(id)}
                                    aria-pressed={active}
                                    className={`flex min-h-16 items-center gap-4 rounded-2xl border px-5 py-4 text-left transition-all ${
                                        active
                                            ? "border-amberx bg-amberx/10 shadow-[0_0_24px_rgba(255,159,28,0.2)]"
                                            : "border-white/15 bg-surface hover:border-amberx/60"
                                    }`}
                                >
                                    <span
                                        className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${
                                            active ? "bg-amberx text-obsidian" : "bg-amberx/15 text-amberx"
                                        }`}
                                    >
                                        {id === "ideas" ? <Lightbulb className="h-5 w-5" /> : <Quote className="h-5 w-5" />}
                                    </span>
                                    <span>
                                        <span className="block font-display text-lg font-bold text-slate-50">{m.label}</span>
                                        <span className="block text-xs text-slate-400">{m.sub}</span>
                                    </span>
                                </button>
                            );
                        })}
                    </div>

                    {mode && (
                        <div
                            data-testid="ai-chat-panel"
                            className="mt-4 overflow-hidden rounded-3xl border border-white/10 bg-obsidian"
                        >
                            <div className="flex items-center justify-between border-b border-white/10 px-5 py-3">
                                <span className="font-mono text-[10px] font-bold tracking-[0.2em] text-amberx">
                                    {MODES[mode].label.toUpperCase()}
                                </span>
                                <div className="flex items-center gap-2">
                                    <button
                                        data-testid="ai-chat-reset-button"
                                        onClick={resetChat}
                                        disabled={busy || messages.length === 0}
                                        className="inline-flex h-9 items-center gap-1.5 rounded-full border border-white/15 px-3 text-xs font-bold text-slate-300 transition-colors hover:border-amberx hover:text-amberx disabled:opacity-40"
                                    >
                                        <RotateCcw className="h-3.5 w-3.5" />
                                        Start over
                                    </button>
                                    <button
                                        data-testid="ai-chat-close-button"
                                        onClick={() => setMode(null)}
                                        aria-label="Close chat"
                                        className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-slate-300 transition-colors hover:border-emberx hover:text-emberx"
                                    >
                                        <X className="h-4 w-4" />
                                    </button>
                                </div>
                            </div>

                            <div
                                ref={scrollRef}
                                data-testid="ai-chat-messages"
                                className="max-h-72 space-y-3 overflow-y-auto px-4 py-4 sm:px-5"
                            >
                                <div className="flex">
                                    <div className="max-w-[85%] rounded-2xl rounded-tl-sm border border-white/10 bg-surface px-4 py-3 text-sm leading-relaxed text-slate-200">
                                        {MODES[mode].intro}
                                    </div>
                                </div>
                                {messages.map((msg, i) =>
                                    msg.role === "user" ? (
                                        <div key={i} className="flex justify-end">
                                            <div className="max-w-[85%] rounded-2xl rounded-tr-sm bg-amberx px-4 py-3 text-sm font-medium leading-relaxed text-obsidian">
                                                {msg.text}
                                            </div>
                                        </div>
                                    ) : (
                                        <div key={i} className="flex">
                                            <div
                                                className={`max-w-[85%] whitespace-pre-line rounded-2xl rounded-tl-sm border px-4 py-3 text-sm leading-relaxed ${
                                                    msg.failed
                                                        ? "border-emberx/50 bg-emberx/10 text-red-200"
                                                        : "border-white/10 bg-surface text-slate-200"
                                                }`}
                                            >
                                                {msg.text || (
                                                    <span className="flex gap-1 py-1">
                                                        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-amberx [animation-delay:0ms]" />
                                                        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-amberx [animation-delay:150ms]" />
                                                        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-amberx [animation-delay:300ms]" />
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    ),
                                )}
                            </div>

                            <div className="flex items-center gap-2 border-t border-white/10 p-3">
                                <input
                                    data-testid="ai-chat-input"
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter" && !e.shiftKey) {
                                            e.preventDefault();
                                            send();
                                        }
                                    }}
                                    maxLength={300}
                                    placeholder={MODES[mode].placeholder}
                                    className="h-12 min-w-0 flex-1 rounded-full border border-white/15 bg-surface px-5 text-base text-slate-100 placeholder:text-slate-500 focus:border-amberx focus:outline-none"
                                />
                                <button
                                    data-testid="ai-chat-send-button"
                                    onClick={send}
                                    disabled={busy || !input.trim()}
                                    aria-label="Send"
                                    className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-amberx text-obsidian transition-transform hover:scale-105 active:scale-95 disabled:cursor-not-allowed disabled:opacity-40"
                                >
                                    <Send className="h-5 w-5" />
                                </button>
                            </div>
                        </div>
                    )}
                </motion.div>
            </div>
        </section>
    );
}
